import {
  defaultGridOptions,
  defaultSingleSelectionHeaderColumn,
} from '@/constants/ag-grid-scenario-builder.constant'
import {
  SCENARIO_CURVE_TYPES,
  YieldCurveShockTypes,
} from '@/enums/scenario-builder.enum'
import { ScenarioAccordionProps } from '@/interfaces/scenarioBuilder.interface'
import { resetSubsetScreening } from '@/states/candidateScreen.slice'
import { setSaveButtonDisabled } from '@/states/scenarioBuilder.slice'
import { ColDef, ModuleRegistry } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { RichSelectModule } from '@ag-grid-enterprise/rich-select'
import {
  Add,
  ContentCopy,
  DeleteOutline,
  ExpandMore,
} from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import SubsetCandidateScreenModal from '../../Base/modals/SubsetCandidateScreenModal'
import KeyRatesToggleConfirmationModal from '../../KeyratesToggleConfirmationModal/keyratesToggleConfirmation.modal'
import { getAvailableCurrencies } from './currency.utils'
import ScenarioTemplateGridMenu from './scenarioTemplateGridMenu'

ModuleRegistry.registerModules([RichSelectModule])

const ScenarioAccordion = ({
  scenario,
  scenarioIndex,
  isReadonly,
  defaultExpanded,
  totalScenarios,
  onYieldCurveShockUpdate,
  onSpreadsShockUpdate,
  onEquityShockUpdate,
  onPrepaymentShockUpdate,
  onVolatilityShockUpdate,
  onShockTypeChange,
  onScenarioNameChange,
  onAddShock,
  onDeleteScenario,
  onDuplicateScenario,
  onAddFxShock,
  onAddSpreadsShock,
  onAddEquityShock,
  onAddPrepaymentShock,
  onAddVolatilityShock,
  onDeleteYieldCurveShockRow,
  onDeleteFxShockRow,
  onDeleteSpreadsShockRow,
  onDeleteEquityShockRow,
  onDeletePrepaymentShockRow,
  onDeleteVolatilityShockRow,
  onBothTypesOff,
  hasError,
  hasDuplicateSubsetNames,
  onClearDuplicateSubsetError,
}: ScenarioAccordionProps): React.ReactElement => {
  const [scenarioCurveType, setScenarioCurveType] = useState<string>(
    SCENARIO_CURVE_TYPES.YIELD_CURVE,
  )
  const [toggleAccordion, setToggleAccordion] =
    useState<boolean>(defaultExpanded)
  const dispatch = useDispatch()

  const [selectedYieldCurveShockType, setSelectedYieldCurveShockType] =
    useState(YieldCurveShockTypes.D_VALUES)

  const [
    keyRatesToggleConfirmationModalOpened,
    setKeyRatesToggleConfirmationModalOpened,
  ] = useState(false)

  const [subsetModalOpen, setSubsetModalOpen] = useState(false)
  const [equitySubsetModalOpen, setEquitySubsetModalOpen] = useState(false)
  const [prepaymentSubsetModalOpen, setPrepaymentSubsetModalOpen] = useState(false)
  const [volatilitySubsetModalOpen, setVolatilitySubsetModalOpen] = useState(false)
  const [wasModalOpen, setWasModalOpen] = useState(false)
  const [hasProcessedCriteria, setHasProcessedCriteria] = useState(false)
  
  
  // Redux selector to get screen criteria from candidate screen state
  const candidateScreenState = useSelector((state: any) => state.candidateScreen)

  // Helper function to get criteria that belong to a specific shock type
  const getCriteriaForShockType = (shockType: 'spreads' | 'equity' | 'prepayment' | 'volatility', allShocks: any[]) => {
    try {
      const templateData = (window as any).__TEMPLATE_SCREENING_DATA__
      if (!templateData) {
        return allShocks
      }
      
      let screeningTitles: any[] = []
      switch (shockType) {
        case 'spreads':
          screeningTitles = templateData.spreadScreeningTitles || []
          break
        case 'equity':
          screeningTitles = templateData.equityScreeningTitles || []
          break
        case 'prepayment':
          screeningTitles = templateData.prepaymentScreeningTitles || []
          break
        case 'volatility':
          screeningTitles = templateData.volatilityScreeningTitles || []
          break
      }
      
      // If no screening titles for this shock type, show all shocks
      if (screeningTitles.length === 0) {
        return allShocks
      }
      
      // Get the criteria names that belong to this shock type
      const validCriteriaNames = screeningTitles.map((title: any) => title.criteriaName)
      
      // Filter shocks to only show those that belong to this shock type
      return allShocks.filter((shock: any) => validCriteriaNames.includes(shock.subsetName))
      
    } catch (error) {
      // Fallback: show all shocks if filtering fails
      return allShocks
    }
  }

  const restrictedCurveTypes: string[] = []

  useEffect(() => {
    dispatch(setSaveButtonDisabled(false))
  }, [scenarioCurveType, scenario.fxShocks, scenario.yieldCurveShocks, dispatch])

  // Track modal state changes
  useEffect(() => {
    if (subsetModalOpen) {
      setWasModalOpen(true)
      setHasProcessedCriteria(false) 
    }
  }, [subsetModalOpen])

  // Yield curve cell value change handler
  const handleYieldCurveCellValueChanged = (params: any) => {
    const { column, newValue, rowIndex } = params
    const field = column.getColId()
    const numericValue = Number(newValue)
    if (!isNaN(numericValue)) {
      onYieldCurveShockUpdate(scenarioIndex, rowIndex, field, numericValue)
    }
  }

  // Spreads cell value change handler
  const handleSpreadsCellValueChanged = (params: any) => {
    const { column, newValue, rowIndex } = params
    const field = column.getColId()
    
    // Handle string fields (like subsetName) and numeric fields
    if (field === 'subsetName' || field === 'screenCriteria') {
      onSpreadsShockUpdate(scenarioIndex, rowIndex, field, newValue)
    } else {
      const numericValue = Number(newValue)
      if (!isNaN(numericValue)) {
        onSpreadsShockUpdate(scenarioIndex, rowIndex, field, numericValue)
      }
    }
  }

  // Equity cell value change handler
  const handleEquityCellValueChanged = (params: any) => {
    const { column, newValue, rowIndex } = params
    const field = column.getColId()
    
    // Handle string fields (like subsetName) and numeric fields
    if (field === 'subsetName' || field === 'screenCriteria') {
      onEquityShockUpdate(scenarioIndex, rowIndex, field, newValue)
    } else {
      const numericValue = Number(newValue)
      if (!isNaN(numericValue)) {
        onEquityShockUpdate(scenarioIndex, rowIndex, field, numericValue)
      }
    }
  }

  // Prepayment cell value change handler
  const handlePrepaymentCellValueChanged = (params: any) => {
    const { column, newValue, rowIndex } = params
    const field = column.getColId()
    
    // Handle string fields (like subsetName) and numeric fields
    if (field === 'subsetName' || field === 'screenCriteria') {
      onPrepaymentShockUpdate(scenarioIndex, rowIndex, field, newValue)
    } else {
      const numericValue = Number(newValue)
      if (!isNaN(numericValue)) {
        onPrepaymentShockUpdate(scenarioIndex, rowIndex, field, numericValue)
      }
    }
  }

  // Volatility cell value change handler
  const handleVolatilityCellValueChanged = (params: any) => {
    const { column, newValue, rowIndex } = params
    const field = column.getColId()
    
    // Handle string fields (like subsetName) and numeric fields
    if (field === 'subsetName' || field === 'screenCriteria') {
      onVolatilityShockUpdate(scenarioIndex, rowIndex, field, newValue)
    } else {
      const numericValue = Number(newValue)
      if (!isNaN(numericValue)) {
        onVolatilityShockUpdate(scenarioIndex, rowIndex, field, numericValue)
      }
    }
  }

  // Helper function for column styling and disabling the inactive columns
  const getColumnProperties = (isKeyRateColumn: boolean) => {
    const isEnabled = isKeyRateColumn
      ? selectedYieldCurveShockType === YieldCurveShockTypes.KEY_RATES
      : selectedYieldCurveShockType === YieldCurveShockTypes.D_VALUES

    return {
      editable: !isReadonly && isEnabled,
      cellStyle: {
        backgroundColor: !isEnabled ? '#f5f5f5' : undefined,
        color: !isEnabled ? '#999' : undefined,
        cursor: !isEnabled ? 'not-allowed' : 'pointer',
      },
      cellEditorParams: {
        min: -9999.99,
        max: 9999.99,
        precision: 2,
        allowNegative: true,
      },
    }
  }

  const yieldCurveGridOptions: any = {
    ...defaultGridOptions,
    onCellValueChanged: (params: any) => handleYieldCurveCellValueChanged(params),
    noRowsOverlayComponentParams: () => ({
      noRowsMessage: 'No yield curve data available.',
    }),
    // Adding popupParent, so that the popup is not cut off because grid container is small
    popupParent: document.body,
    columnDefs: [
      {
        ...defaultSingleSelectionHeaderColumn,
        hide: isReadonly,
        editable: false,
        cellRenderer: (params: any) => {
          return (
            <ScenarioTemplateGridMenu
              {...params}
              isReadonly={isReadonly}
              onDeleteRow={() => {
                const rowIndex = params.node.rowIndex
                onDeleteYieldCurveShockRow(scenarioIndex, rowIndex)
              }}
              tableIdentifier='Curve'
            />
          )
        },
      },
      {
        headerName: 'Yield Curve',
        field: 'curve',
        cellEditor: 'agRichSelectCellEditor',
        cellEditorPopup: true,
        cellEditorParams: {
          allowTyping: true,
          filterList: true,
          highlightMatch: true,
          values: (params: any) =>
            getAvailableCurrencies(
              scenario.yieldCurveShocks.map((shock) => shock.curve),
              params.rowIndex,
            ),
        },
        editable: !isReadonly,
        flex: 2.3,
        suppressKeyboardEvent: (params: any) => {
          return params.editing
        },
        cellStyle: { overflow: 'visible', cursor: 'pointer' },
      },
      {
        headerName: 'Level',
        field: 'd1',
        ...getColumnProperties(false),
        flex: 1.5,
      },
      {
        headerName: 'Slope',
        field: 'd2',
        ...getColumnProperties(false),
        flex: 1.5,
      },
      {
        headerName: 'Curvature',
        field: 'd3',
        ...getColumnProperties(false),
        flex: 2,
      },
      {
        headerName: '3M',
        field: 'k0_2',
        ...getColumnProperties(true),
      },
      {
        headerName: '6M',
        field: 'k0_5',
        ...getColumnProperties(true),
      },
      {
        headerName: '1Y',
        field: 'k1',
        ...getColumnProperties(true),
      },
      {
        headerName: '2Y',
        field: 'k2',
        ...getColumnProperties(true),
      },
      {
        headerName: '3Y',
        field: 'k3',
        ...getColumnProperties(true),
      },
      {
        headerName: '4Y',
        field: 'k4',
        ...getColumnProperties(true),
      },
      {
        headerName: '5Y',
        field: 'k5',
        ...getColumnProperties(true),
      },
      {
        headerName: '7Y',
        field: 'k7',
        ...getColumnProperties(true),
      },
      {
        headerName: '10Y',
        field: 'k10',
        ...getColumnProperties(true),
      },
      {
        headerName: '15Y',
        field: 'k15',
        ...getColumnProperties(true),
      },
      {
        headerName: '20Y',
        field: 'k20',
        ...getColumnProperties(true),
      },
      {
        headerName: '25Y',
        field: 'k25',
        ...getColumnProperties(true),
      },
      {
        headerName: '30Y',
        field: 'k30',
        ...getColumnProperties(true),
      },
      {
        headerName: '40Y',
        field: 'k40',
        ...getColumnProperties(true),
      },
    ] as ColDef[],
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fxGridOptions: any = {
    ...defaultGridOptions,
    columnDefs: [
      {
        ...defaultSingleSelectionHeaderColumn,
        hide: isReadonly, // conditionally rendering hamburger menu in YieldCurve Grid
        editable: false,
        cellRenderer: (params: any) => {
          return (
            <ScenarioTemplateGridMenu
              {...params}
              isReadonly={isReadonly}
              onDeleteRow={() => {
                const rowIndex = params.node.rowIndex
                onDeleteFxShockRow(scenarioIndex, rowIndex)
              }}
              tableIdentifier='Currency'
            />
          )
        },
      },
      {
        headerName: 'Currency',
        field: 'currency',
        cellEditor: 'agRichSelectCellEditor',
        cellEditorPopup: true,
        cellStyle: { cursor: 'pointer' },
        cellEditorParams: {
          values: (params: any) =>
            getAvailableCurrencies(
              scenario.fxShocks.map((shock) => shock.currency),
              params.rowIndex,
            ),
          allowTyping: true,
          filterList: true,
          highlightMatch: true,
          popupParent: document.body,
        },
      },
      {
        headerName: 'Currency Return',
        field: 'currReturn',
        cellStyle: { cursor: 'pointer' },
      },
    ] as ColDef[],
  }

  // Spreads Grid Options
  const spreadsGridOptions: any = {
    ...defaultGridOptions,
    onCellValueChanged: (params: any) => handleSpreadsCellValueChanged(params),
    columnDefs: [
      {
        ...defaultSingleSelectionHeaderColumn,
        hide: isReadonly,
        editable: false,
        cellRenderer: (params: any) => (
          <ScenarioTemplateGridMenu
            {...params}
            isReadonly={isReadonly}
            onDeleteRow={() => {
              const rowIndex = params.node.rowIndex
              onDeleteSpreadsShockRow(scenarioIndex, rowIndex)
            }}
            tableIdentifier='Spreads'
          />
        ),
      },
      {
        headerName: 'Subset Name',
        field: 'subsetName',
        editable: !isReadonly,
        flex: 2,
      },
      {
        headerName: 'Screen Criteria',
        field: 'screenCriteria',
        editable: false,
        flex: 2.5,
        cellStyle: { color: '#666', fontStyle: 'italic' },
      },
      {
        headerName: 'Factor Shift',
        field: 'factorShift',
        editable: !isReadonly,
        cellDataType: 'number',
        flex: 1.5,
      },
      {
        headerName: 'Offset Shift',
        field: 'offsetShift',
        editable: !isReadonly,
        cellDataType: 'number',
        flex: 1.5,
      },
    ] as ColDef[],
  }

  // Equity Grid Options
  const equityGridOptions: any = {
    ...defaultGridOptions,
    onCellValueChanged: (params: any) => handleEquityCellValueChanged(params),
    columnDefs: [
      {
        ...defaultSingleSelectionHeaderColumn,
        hide: isReadonly,
        editable: false,
        cellRenderer: (params: any) => (
          <ScenarioTemplateGridMenu
            {...params}
            isReadonly={isReadonly}
            onDeleteRow={() => {
              const rowIndex = params.node.rowIndex
              onDeleteEquityShockRow(scenarioIndex, rowIndex)
            }}
            tableIdentifier='Equity'
          />
        ),
      },
      {
        headerName: 'Subset Name',
        field: 'subsetName',
        editable: !isReadonly,
        flex: 2,
      },
      {
        headerName: 'Screen Criteria',
        field: 'screenCriteria',
        editable: false,
        flex: 2.5,
        cellStyle: { color: '#666', fontStyle: 'italic' },
      },
      {
        headerName: 'Equity Market Shock',
        field: 'eqtyMktShock',
        editable: !isReadonly,
        cellDataType: 'number',
        flex: 2,
      },
      {
        headerName: 'Subset Return Override',
        field: 'subsetReturnOverride',
        editable: !isReadonly,
        cellDataType: 'number',
        flex: 2,
      },
    ] as ColDef[],
  }

  // Prepayment Grid Options
  const prepaymentGridOptions: any = {
    ...defaultGridOptions,
    onCellValueChanged: (params: any) => handlePrepaymentCellValueChanged(params),
    columnDefs: [
      {
        ...defaultSingleSelectionHeaderColumn,
        hide: isReadonly,
        editable: false,
        cellRenderer: (params: any) => (
          <ScenarioTemplateGridMenu
            {...params}
            isReadonly={isReadonly}
            onDeleteRow={() => {
              const rowIndex = params.node.rowIndex
              onDeletePrepaymentShockRow(scenarioIndex, rowIndex)
            }}
            tableIdentifier='Prepayment'
          />
        ),
      },
      {
        headerName: 'Subset Name',
        field: 'subsetName',
        editable: !isReadonly,
        flex: 2,
      },
      {
        headerName: 'Screen Criteria',
        field: 'screenCriteria',
        editable: false,
        flex: 2.5,
        cellStyle: { color: '#666', fontStyle: 'italic' },
      },
      {
        headerName: 'Prepay Value',
        field: 'prepayVal',
        editable: !isReadonly,
        cellDataType: 'number',
        flex: 1.5,
      },
      {
        headerName: 'Prepay Type',
        field: 'prepayType',
        editable: !isReadonly,
        cellDataType: 'number',
        flex: 1.5,
      },
    ] as ColDef[],
  }

  // Volatility Grid Options
  const volatilityGridOptions: any = {
    ...defaultGridOptions,
    onCellValueChanged: (params: any) => handleVolatilityCellValueChanged(params),
    columnDefs: [
      {
        ...defaultSingleSelectionHeaderColumn,
        hide: isReadonly,
        editable: false,
        cellRenderer: (params: any) => (
          <ScenarioTemplateGridMenu
            {...params}
            isReadonly={isReadonly}
            onDeleteRow={() => {
              const rowIndex = params.node.rowIndex
              onDeleteVolatilityShockRow(scenarioIndex, rowIndex)
            }}
            tableIdentifier='Volatility'
          />
        ),
      },
      {
        headerName: 'Subset Name',
        field: 'subsetName',
        editable: !isReadonly,
        flex: 2,
      },
      {
        headerName: 'Screen Criteria',
        field: 'screenCriteria',
        editable: false,
        flex: 2.5,
        cellStyle: { color: '#666', fontStyle: 'italic' },
      },
      {
        headerName: 'Default Mult',
        field: 'defaultMult',
        editable: !isReadonly,
        cellDataType: 'number',
        flex: 1.5,
      },
      {
        headerName: 'Recovery Mult',
        field: 'recoveryMult',
        editable: !isReadonly,
        cellDataType: 'number',
        flex: 1.5,
      },
      {
        headerName: 'Short Vol Mult',
        field: 'shortVolMult',
        editable: !isReadonly,
        cellDataType: 'number',
        flex: 1.5,
      },
      {
        headerName: 'Long Vol Mult',
        field: 'longVolMult',
        editable: !isReadonly,
        cellDataType: 'number',
        flex: 1.5,
      },
    ] as ColDef[],
  }



  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setScenarioCurveType(newValue)
    onBothTypesOff(scenarioIndex, false, [newValue])
  }

  const handleDuplicateScenario = (e: any, scenarioIndex: number) => {
    e.stopPropagation()
    onDuplicateScenario(scenarioIndex)
  }

  const handleDeleteScenario = (e: any, scenarioIndex: number) => {
    e.stopPropagation()
    onDeleteScenario(scenarioIndex)
  }

  const handleShockTypeChanged = () => {
    const updatedShockType =
      selectedYieldCurveShockType === YieldCurveShockTypes.KEY_RATES
        ? YieldCurveShockTypes.D_VALUES
        : YieldCurveShockTypes.KEY_RATES
    setSelectedYieldCurveShockType(updatedShockType)
    onShockTypeChange(scenarioIndex, updatedShockType)
  }

  return (
    <Accordion
      expanded={toggleAccordion}
      onChange={(e: any, expanded: any) => setToggleAccordion(expanded)}
      disableGutters
      data-cy={`scenario-temp-accordion-container`}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          gap={1}
          width='100%'
          data-cy='scenario-temp-accordion'
        >
          {!isReadonly ? (
            <Tooltip
              title={hasError ? 'Scenario Name should not be Duplicate' : ''}
              placement='top'
              arrow
              disableHoverListener={!hasError}
            >
              <span>
                <TextField
                  data-cy='scenario-name-field'
                  size='small'
                  label='Scenario Name'
                  type='text'
                  value={scenario?.name}
                  onClick={(e: any) => e.stopPropagation()}
                  onChange={(e: any) => {
                    onScenarioNameChange(e.target.value, scenarioIndex)
                  }}
                  error={hasError}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: hasError ? 'red' : '',
                      },
                    },
                  }}
                />
              </span>
            </Tooltip>
          ) : (
            <Typography variant='h3'>{scenario?.name}</Typography>
          )}
          <Box display='flex' alignItems='center' gap={1}>
            {!isReadonly && (
              <Tooltip title={`Duplicate ${scenario?.name}`} placement='top'>
                <Button
                  size='small'
                  data-cy='duplicate-scenario-button'
                  onClick={(e) => handleDuplicateScenario(e, scenarioIndex)}
                  sx={{ minWidth: 'auto', p: 1 }}
                  disabled={scenarioCurveType.length === 0}
                  data-testid='copy-scenario-button'
                >
                  <ContentCopy sx={{ fontSize: '20px' }} />
                </Button>
              </Tooltip>
            )}
            {!isReadonly && totalScenarios > 1 && (
              <Tooltip title={`Delete ${scenario?.name}`} placement='top'>
                <Button
                  size='small'
                  data-cy='delete-scenario-button'
                  onClick={(e) => handleDeleteScenario(e, scenarioIndex)}
                  sx={{ minWidth: 'auto', color: 'error.main' }}
                  disabled={scenarioCurveType.length === 0}
                  data-testid='delete-scenario-button'
                >
                  <DeleteOutline />
                </Button>
              </Tooltip>
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ margin: 0, p: 0, pb: 2 }}>
        <Box
          borderBottom='1px solid #ccc'
          bgcolor='background.default'
        >
          <Tabs
            value={scenarioCurveType}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
              },
              minHeight: 48
            }}
          >
            {Object.values(SCENARIO_CURVE_TYPES).map((type) => (
              <Tab
                key={type}
                label={type}
                value={type}
                disabled={isReadonly || restrictedCurveTypes.includes(type)}
                data-cy={`${type.replace(' ', '-').toLowerCase()}-shock-tab`}
                sx={{
                  textTransform: 'none',
                  minWidth: 120,
                  '&.Mui-disabled': {
                    opacity: 0.5,
                  }
                }}
              />
            ))}
          </Tabs>
        </Box>

        <Box
          display='flex'
          flexDirection='column'
          minHeight='300px'
          maxHeight='400px'
          padding={1}
        >
          <Box
            display={
              scenarioCurveType === SCENARIO_CURVE_TYPES.YIELD_CURVE
                ? 'block'
                : 'none'
            }
          >
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              py={1}
            >
              <Box display='flex' alignItems='center' gap={2}>
                <Typography variant='h5' marginLeft={1}>
                  Yield Curve Shock (%)
                </Typography>

                {/* Shock type switch (dvalues or keyrates)*/}
                <FormControlLabel
                  control={
                    <Switch
                      disabled={isReadonly}
                      checked={
                        selectedYieldCurveShockType ===
                        YieldCurveShockTypes.KEY_RATES
                      }
                      onChange={() =>
                        setKeyRatesToggleConfirmationModalOpened(true)
                      }
                      name='shock-type'
                      inputProps={{ 'aria-label': 'shock type' }}
                      data-cy='yield-curve-shock-type-switch'
                    />
                  }
                  label={'Use Key Rates'}
                />
              </Box>
              <Button
                // disable this button if we have created row for all available currencies.
                disabled={
                  isReadonly ||
                  getAvailableCurrencies(
                    scenario.yieldCurveShocks.map((shock) => shock.curve),
                  ).length === 0
                }
                startIcon={<Add />}
                onClick={() => onAddShock(scenarioIndex)}
                variant='outlined'
                size='small'
                data-cy='add-yield-curve-button'
              >
                Add Yield Curve
              </Button>
            </Box>

            <Box
              className='ag-theme-quartz'
              boxSizing='border-box'
              data-cy='yield-curve-grid-table-container'
            >
              <AgGridReact
                data-testid='yield-curve-grid'
                data-cy='yield-curve-grid-table'
                {...yieldCurveGridOptions}
                defaultColDef={{
                  ...yieldCurveGridOptions.defaultColDef,
                  editable: !isReadonly,
                  enablePaste: !isReadonly,
                  suppressPaste: isReadonly,
                }}
                rowData={scenario.yieldCurveShocks}
              />
            </Box>
          </Box>

          <Box
            display={
              scenarioCurveType === SCENARIO_CURVE_TYPES.FX
                ? 'block'
                : 'none'
            }
          >
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              py={4}
            >
              <Typography variant='h5' marginLeft={1}>
                FX Shock (%)
              </Typography>
              <Button
                // disable this button if a row has already been added for all available currencies
                disabled={
                  isReadonly ||
                  getAvailableCurrencies(
                    scenario.fxShocks.map((shock) => shock.currency),
                  ).length === 0
                }
                startIcon={<Add />}
                onClick={() => onAddFxShock(scenarioIndex)}
                variant='outlined'
                size='small'
              >
                Add Currency
              </Button>
            </Box>
            <Box className='ag-theme-quartz'>
              <AgGridReact
                {...fxGridOptions}
                defaultColDef={{
                  ...defaultGridOptions.defaultColDef,
                  editable: !isReadonly,
                  enablePaste: !isReadonly,
                  suppressPaste: isReadonly,
                }}
                rowData={scenario.fxShocks}
                data-cy='fx-grid-table'
                data-testid='fx-grid'
              />
            </Box>
          </Box>

          {/* Spreads Tab */}
          <Box
            display={
              scenarioCurveType === SCENARIO_CURVE_TYPES.SPREADS
                ? 'block'
                : 'none'
            }
          >
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              py={4}
            >
              <Typography variant='h5' marginLeft={1}>
                Spreads Shock
              </Typography>
              <Button
                disabled={isReadonly}
                startIcon={<Add />}
                onClick={() => {
                  setSubsetModalOpen(true)
                }}
                variant='outlined'
                size='small'
                data-cy='add-spreads-button'
              >
                Add Spreads
              </Button>
            </Box>
            <Box className='ag-theme-quartz'>
              <AgGridReact
                {...spreadsGridOptions}
                defaultColDef={{
                  ...defaultGridOptions.defaultColDef,
                  editable: !isReadonly,
                  enablePaste: !isReadonly,
                  suppressPaste: isReadonly,
                }}
                rowData={getCriteriaForShockType('spreads', scenario.spreadsShocks || [])}
                data-cy='spreads-grid-table'
                data-testid='spreads-grid'
              />
            </Box>
          </Box>

          {/* Equity Tab */}
          <Box
            display={
              scenarioCurveType === SCENARIO_CURVE_TYPES.EQUITY
                ? 'block'
                : 'none'
            }
          >
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              py={4}
            >
              <Typography variant='h5' marginLeft={1}>
                Equity Shock
              </Typography>
              <Button
                disabled={isReadonly}
                startIcon={<Add />}
                onClick={() => {
                  setEquitySubsetModalOpen(true)
                }}
                variant='outlined'
                size='small'
                data-cy='add-equity-button'
              >
                Add Equity
              </Button>
            </Box>
            <Box className='ag-theme-quartz'>
              <AgGridReact
                {...equityGridOptions}
                defaultColDef={{
                  ...defaultGridOptions.defaultColDef,
                  editable: !isReadonly,
                  enablePaste: !isReadonly,
                  suppressPaste: isReadonly,
                }}
                rowData={getCriteriaForShockType('equity', scenario.equityShocks || [])}
                data-cy='equity-grid-table'
                data-testid='equity-grid'
              />
            </Box>
          </Box>

          {/* Prepayment Tab */}
          <Box
            display={
              scenarioCurveType === SCENARIO_CURVE_TYPES.PREPAYMENT
                ? 'block'
                : 'none'
            }
          >
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              py={4}
            >
              <Typography variant='h5' marginLeft={1}>
                Prepayment Shock
              </Typography>
              <Button
                disabled={isReadonly}
                startIcon={<Add />}
                onClick={() => {
                  setPrepaymentSubsetModalOpen(true)
                }}
                variant='outlined'
                size='small'
                data-cy='add-prepayment-button'
              >
                Add Prepayment
              </Button>
            </Box>
            <Box className='ag-theme-quartz'>
              <AgGridReact
                {...prepaymentGridOptions}
                defaultColDef={{
                  ...defaultGridOptions.defaultColDef,
                  editable: !isReadonly,
                  enablePaste: !isReadonly,
                  suppressPaste: isReadonly,
                }}
                rowData={getCriteriaForShockType('prepayment', scenario.prepaymentShocks || [])}
                data-cy='prepayment-grid-table'
                data-testid='prepayment-grid'
              />
            </Box>
          </Box>

          {/* Volatility Tab */}
          <Box
            display={
              scenarioCurveType === SCENARIO_CURVE_TYPES.VOLATILITY
                ? 'block'
                : 'none'
            }
          >
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              py={4}
            >
              <Typography variant='h5' marginLeft={1}>
                Volatility Shock
              </Typography>
              <Button
                disabled={isReadonly}
                startIcon={<Add />}
                onClick={() => {
                  setVolatilitySubsetModalOpen(true)
                }}
                variant='outlined'
                size='small'
                data-cy='add-volatility-button'
              >
                Add Volatility
              </Button>
            </Box>
            <Box className='ag-theme-quartz'>
              <AgGridReact
                {...volatilityGridOptions}
                defaultColDef={{
                  ...defaultGridOptions.defaultColDef,
                  editable: !isReadonly,
                  enablePaste: !isReadonly,
                  suppressPaste: isReadonly,
                }}
                rowData={getCriteriaForShockType('volatility', scenario.volatilityShocks || [])}
                data-cy='volatility-grid-table'
                data-testid='volatility-grid'
              />
            </Box>
          </Box>
        </Box>
      </AccordionDetails>

      {/* Define Subset Modal - Spreads */}
      <SubsetCandidateScreenModal
        isOpen={subsetModalOpen}
        onClose={() => {
          setSubsetModalOpen(false)
        }}
        onApply={(criteriaName: string, criteriaData?: any) => {
          setSubsetModalOpen(false)
          onAddSpreadsShock(scenarioIndex, criteriaName, criteriaData)
          dispatch(resetSubsetScreening())
        }}
        isSubset={true}
        filterTo="S"
      />

      {/* Define Subset Modal - Equity */}
      <SubsetCandidateScreenModal
        isOpen={equitySubsetModalOpen}
        onClose={() => {
          setEquitySubsetModalOpen(false)
        }}
        onApply={(criteriaName: string, criteriaData?: any) => {
          setEquitySubsetModalOpen(false)
          onAddEquityShock(scenarioIndex, criteriaName, criteriaData)
          dispatch(resetSubsetScreening())
        }}
        isSubset={true}
        filterTo="E"
      />

      {/* Define Subset Modal - Prepayment */}
      <SubsetCandidateScreenModal
        isOpen={prepaymentSubsetModalOpen}
        onClose={() => {
          setPrepaymentSubsetModalOpen(false)
        }}
        onApply={(criteriaName: string, criteriaData?: any) => {
          setPrepaymentSubsetModalOpen(false)
          onAddPrepaymentShock(scenarioIndex, criteriaName, criteriaData)
          dispatch(resetSubsetScreening())
        }}
        isSubset={true}
        filterTo="P"
      />

      {/* Define Subset Modal - Volatility */}
      <SubsetCandidateScreenModal
        isOpen={volatilitySubsetModalOpen}
        onClose={() => {
          setVolatilitySubsetModalOpen(false)
        }}
        onApply={(criteriaName: string, criteriaData?: any) => {
          setVolatilitySubsetModalOpen(false)
          onAddVolatilityShock(scenarioIndex, criteriaName, criteriaData)
          dispatch(resetSubsetScreening())
        }}
        isSubset={true}
        filterTo="V"
      />

      <KeyRatesToggleConfirmationModal
        keyRatesToggleConfirmationModalOpened={
          keyRatesToggleConfirmationModalOpened
        }
        setKeyRatesToggleConfirmationModalOpened={
          setKeyRatesToggleConfirmationModalOpened
        }
        currentShockType={selectedYieldCurveShockType}
        setShockType={handleShockTypeChanged}
      />
    </Accordion>
  )
}

export default ScenarioAccordion
